import axios from 'axios'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useSelector} from 'react-redux'

import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import {DataGridPro, GridColDef} from '@mui/x-data-grid-pro'
import Swal from 'sweetalert2'

import {chargeBackHost} from 'GLOBAL/envVariables'
import ManufacturerFormDialog, {
  ManufacturerFormErrorsType,
  ManufacturerFormType,
} from './ManufacturerFormDialog'

type RowType = {
  id: number
  office: string
  cooperIdx: number
  cooperName: string
  active: boolean
}

type StatusFilterType = 'all' | 'active' | 'inactive'

const ManufacturerManagement = () => {
  const officeValue = useSelector((state: any) => state?.officeInfo.value)

  const [rows, setRows] = useState<RowType[]>([])
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilterType>('all')

  const [form, setForm] = useState<ManufacturerFormType>({
    office: officeValue || '',
    cooperIdx: '',
    cooperName: '',
    active: true,
  })

  const [formErrors, setFormErrors] = useState<ManufacturerFormErrorsType>({
    cooperIdx: '',
    cooperName: '',
  })

  const [openFormModal, setOpenFormModal] = useState(false)

  const resetForm = useCallback(() => {
    setForm({
      office: officeValue || '',
      cooperIdx: '',
      cooperName: '',
      active: true,
    })

    setFormErrors({
      cooperIdx: '',
      cooperName: '',
    })
  }, [officeValue])

  const fetchManufacturers = useCallback(async () => {
    if (!officeValue) return

    try {
      const res = await axios.get(`${chargeBackHost}/api/manufacturer/select`, {
        params: {
          office_id: officeValue,
        },
      })

      if (res.data.success) {
        const mapped = res.data.data.map((item: any, idx: number) => ({
          id: idx + 1,
          office: item.office_id ?? officeValue ?? '',
          cooperIdx: item.cooper_idx,
          cooperName: item.cooper_name,
          active: item.used_status,
        }))

        setRows(mapped)
      }
    } catch (err) {
      console.error(err)

      Swal.fire({
        icon: 'error',
        title: '조회 실패',
        text: '제조사 목록을 불러오지 못했습니다.',
        confirmButtonText: '확인',
        confirmButtonColor: '#d32f2f',
      })
    }
  }, [officeValue])

  useEffect(() => {
    if (!officeValue) return

    fetchManufacturers()
  }, [fetchManufacturers, officeValue])

  const validateForm = () => {
    const trimmedCooperName = (form.cooperName ?? '').trim()

    const nextErrors: ManufacturerFormErrorsType = {
      cooperIdx: '',
      cooperName: '',
    }

    if (form.cooperIdx === '' || form.cooperIdx === null || form.cooperIdx === undefined) {
      nextErrors.cooperIdx = '제조사 번호를 입력해주세요.'
    } else if (Number.isNaN(Number(form.cooperIdx))) {
      nextErrors.cooperIdx = '제조사 번호는 숫자로 입력해주세요.'
    }

    if (!trimmedCooperName) {
      nextErrors.cooperName = '제조사명을 입력해주세요.'
    }

    setFormErrors(nextErrors)

    return !nextErrors.cooperIdx && !nextErrors.cooperName
  }

  const handleFormChange = (
    field: keyof ManufacturerFormType,
    value: string | boolean | number
  ) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }))

    if (field === 'cooperIdx' || field === 'cooperName') {
      setFormErrors((prev) => ({
        ...prev,
        [field]: '',
      }))
    }
  }

  const handleOpenAddModal = () => {
    setForm({
      office: officeValue || '',
      cooperIdx: '',
      cooperName: '',
      active: true,
    })

    setFormErrors({
      cooperIdx: '',
      cooperName: '',
    })

    setOpenFormModal(true)
  }

  const handleCloseFormModal = () => {
    setOpenFormModal(false)
    resetForm()
  }

  const toggleStatus = async (row: RowType) => {
    const newStatus = !row.active

    try {
      await axios.patch(
        `${chargeBackHost}/api/manufacturer/update-status`,
        {},
        {
          params: {
            office_id: row.office,
            cooper_idx: row.cooperIdx,
            used_status: newStatus,
          },
        }
      )

      setRows((prev) =>
        prev.map((r) =>
          r.office === row.office && r.cooperIdx === row.cooperIdx
            ? {
                ...r,
                active: newStatus,
              }
            : r
        )
      )
    } catch (err) {
      console.error(err)

      Swal.fire({
        icon: 'error',
        title: '변경 실패',
        text: '제조사 사용 상태를 변경하지 못했습니다.',
        confirmButtonText: '확인',
        confirmButtonColor: '#d32f2f',
      })
    }
  }

  const saveNewRow = async () => {
    const isValid = validateForm()

    if (!isValid) return

    const trimmedCooperName = (form.cooperName ?? '').trim()

    try {
      await axios.post(`${chargeBackHost}/api/manufacturer/create`, {
        office_id: form.office,
        cooper_idx: Number(form.cooperIdx),
        cooper_name: trimmedCooperName,
        used_status: form.active,
      })

      handleCloseFormModal()
      await fetchManufacturers()

      Swal.fire({
        icon: 'success',
        title: '저장 완료',
        text: '제조사가 추가되었습니다.',
        confirmButtonText: '확인',
        confirmButtonColor: '#1976d2',
      })
    } catch (err: any) {
      console.error(err)

      const errorMessage = err?.response?.data?.response || '제조사 추가 중 오류가 발생했습니다.'

      Swal.fire({
        icon: 'error',
        title: '저장 실패',
        text: errorMessage,
        confirmButtonText: '확인',
        confirmButtonColor: '#d32f2f',
      })
    }
  }

  const filteredRows = useMemo(() => {
    const keyword = searchText.trim().toLowerCase()

    return rows.filter((row) => {
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && row.active) ||
        (statusFilter === 'inactive' && !row.active)

      if (!matchesStatus) return false

      if (!keyword) return true

      const office = String(row.office ?? '').toLowerCase()
      const cooperIdx = String(row.cooperIdx ?? '').toLowerCase()
      const cooperName = String(row.cooperName ?? '').toLowerCase()
      const activeText = row.active ? '사용' : '미사용'

      return (
        office.includes(keyword) ||
        cooperIdx.includes(keyword) ||
        cooperName.includes(keyword) ||
        activeText.includes(keyword)
      )
    })
  }, [rows, searchText, statusFilter])

  const columns: GridColDef[] = [
    {
      field: 'office',
      headerName: '사업소 코드',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'cooperIdx',
      headerName: '제조사 번호',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'cooperName',
      headerName: '제조사명',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'active',
      headerName: '사용 상태',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        <Switch
          checked={params.row.active}
          onClick={(e) => e.stopPropagation()}
          onChange={() => toggleStatus(params.row)}
          color='success'
        />
      ),
    },
  ]

  return (
    <Box display='flex' flexDirection='column' minWidth={0} height='100%'>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant='h6'>제조사 관리</Typography>

          <Typography
            variant='body2'
            sx={{
              fontSize: '14px',
              fontWeight: '500',
              color: 'text.secondary',
            }}
          >
            장비 제조사 정보를 등록하고 사용 상태를 관리합니다.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <FormControl size='small' sx={{minWidth: 140}}>
            <InputLabel id='manufacturer-status-filter-label'>사용상태</InputLabel>

            <Select
              labelId='manufacturer-status-filter-label'
              value={statusFilter}
              label='사용상태'
              onChange={(e) => setStatusFilter(e.target.value as StatusFilterType)}
            >
              <MenuItem value='all'>전체</MenuItem>
              <MenuItem value='active'>사용</MenuItem>
              <MenuItem value='inactive'>미사용</MenuItem>
            </Select>
          </FormControl>

          <TextField
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder='검색'
            size='small'
            sx={{width: 280}}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon fontSize='small' />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant='contained'
            onClick={handleOpenAddModal}
            disabled={!officeValue}
            sx={{
              height: 48,
              minWidth: 120,
              whiteSpace: 'nowrap',
            }}
          >
            제조사 등록
          </Button>
        </Box>
      </Box>

      <Box sx={{flex: 1, minHeight: 0}}>
        <DataGridPro
          rows={filteredRows}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
          disableSelectionOnClick
          initialState={{
            sorting: {
              sortModel: [
                {
                  field: 'cooperIdx',
                  sort: 'asc',
                },
              ],
            },
          }}
          sx={{
            '& .MuiDataGrid-root, & .MuiDataGrid-columnHeaders': {
              fontFamily: 'Noto Sans KR, sans-serif',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f3f4f6',
            },
            '& .MuiDataGrid-cell': {
              fontSize: '16px',
              fontWeight: '400',
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
          }}
        />
      </Box>

      <ManufacturerFormDialog
        open={openFormModal}
        form={form}
        errors={formErrors}
        onClose={handleCloseFormModal}
        onChange={handleFormChange}
        onSubmit={saveNewRow}
      />
    </Box>
  )
}

export default ManufacturerManagement
